import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './style.css';
import { FiMoreVertical, FiImage, FiEdit3, FiClock, FiUser, FiFileText, FiChevronDown } from 'react-icons/fi';
import { createBlog } from '../../store/Blog/blogSlice';
import { fetchCategories, createCategory } from '../../store/Blog/categoriesSlice';
import { fetchTags, createTag } from '../../store/Blog/tagsSlice';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import React Quill styles

const CreatePost = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading: blogLoading, error: blogError } = useSelector(state => state.blogs);
  const { categories, loading: catLoading, error: catError } = useSelector(state => state.blogCategories);
  const { tags, loading: tagLoading, error: tagError } = useSelector(state => state.tags);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    status: 'Draft',
    categories: [],
    tags: [],
    featuredImage: null,
    galleryImages: [],
  });
  const [newTag, setNewTag] = useState('');
  const [featuredImagePreview, setFeaturedImagePreview] = useState('');
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchTags());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle React Quill content change
  const handleContentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      content: value,
    }));
  };

  const handleFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Featured image must be under 10MB');
        return;
      }
      setFormData(prev => ({ ...prev, featuredImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturedImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const validFiles = files.filter(file => {
        if (file.size > 10 * 1024 * 1024) {
          alert(`Image ${file.name} exceeds 10MB and will be skipped`);
          return false;
        }
        return true;
      });
      setFormData(prev => ({ ...prev, galleryImages: validFiles }));
      const previews = [];
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          if (previews.length === validFiles.length) {
            setGalleryPreviews(previews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => {
      const newCategories = prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId];
      return { ...prev, categories: newCategories };
    });
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => {
      const newTags = prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId];
      return { ...prev, tags: newTags };
    });
  };

  const handleAddNewCategory = async () => {
    const newCategory = prompt('Enter new category name:');
    if (newCategory?.trim()) {
      try {
        const result = await dispatch(createCategory({ name: newCategory.trim() })).unwrap();
        setFormData(prev => ({
          ...prev,
          categories: [...prev.categories, result._id],
        }));
      } catch (err) {
        alert(`Error creating category: ${err.message || err}`);
      }
    }
  };

  const handleAddNewTag = async () => {
    if (newTag.trim()) {
      try {
        const result = await dispatch(createTag({ name: newTag.trim() })).unwrap();
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, result._id],
        }));
        setNewTag('');
      } catch (err) {
        alert(`Error creating tag: ${err.message || err}`);
      }
    }
  };

  const handleSubmit = async (isPublish = false) => {
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('content', formData.content);
    if (formData.excerpt) formDataToSend.append('excerpt', formData.excerpt);
    formDataToSend.append('status', isPublish ? 'Published' : 'Draft');
    if (isPublish) formDataToSend.append('publishDate', new Date().toISOString());
    formData.categories.forEach(cat => formDataToSend.append('categories', cat));
    formData.tags.forEach(tag => formDataToSend.append('tags', tag));
    if (formData.featuredImage) formDataToSend.append('featuredImage', formData.featuredImage);
    formData.galleryImages.forEach(img => formDataToSend.append('galleryImages', img));

    // Log FormData and calculate size
    let totalSize = 0;
    console.log('Submitting FormData:');
    for (const [key, value] of formDataToSend.entries()) {
      const size = value instanceof File ? value.size : new TextEncoder().encode(value).length;
      totalSize += size;
      console.log(`FormData: ${key} = ${value instanceof File ? `${value.name} (${(size / 1024).toFixed(2)} KB)` : value}`);
    }
    console.log(`Total FormData size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);

    try {
      await dispatch(createBlog(formDataToSend)).unwrap();
      console.log('Post created successfully');
      navigate('/blogs');
    } catch (err) {
      console.error('Submission error:', err);
      alert(`Error: ${err.message || 'Failed to create post'}`);
    }
  };

  const handleSaveDraft = () => handleSubmit(false);
  const handlePublish = () => handleSubmit(true);

  // React Quill toolbar options
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  return (
    <div className="create-post product-wrap col-md-10 mx-auto mt-3 p-3">
      <div className="container">
        <div className="row">
          <div className="new-post-header">
            <h2>Create New Post</h2>
            <div className="nph-2">
              <button
                className="save-draft-btn"
                onClick={handleSaveDraft}
                disabled={blogLoading || !formData.title.trim()}
              >
                {blogLoading ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                className="btn-cls"
                onClick={handlePublish}
                disabled={blogLoading || !formData.title.trim()}
              >
                {blogLoading ? 'Publishing...' : 'Publish'}
              </button>
              <FiMoreVertical size={20} className="ms-2" />
            </div>
          </div>

          {(blogError || catError || tagError) && (
            <div className="alert alert-danger">{blogError || catError || tagError}</div>
          )}

          {(blogLoading || catLoading || tagLoading) && (
            <div className="text-center my-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <div className="col-md-9">
            <input
              className="add-title-text form-control mb-3"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Add Title"
              required
            />
            <ReactQuill
              style={{minHeight : "200px"}}
              value={formData.content}
              onChange={handleContentChange}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Start writing your post..."
              className="mb-3"
            />
<style>
  {`
    .ql-editor {
      min-height: 200px;
    }
  `}
</style>
            <div className="lte-sec mt-3">
              <h5>Featured Image</h5>
              <label className="btn btn-outline-secondary mb-3">
                <FiImage size={20} className="me-2" />
                {formData.featuredImage ? 'Change Image' : 'Add Featured Image'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFeaturedImageChange}
                  style={{ display: 'none' }}
                />
              </label>
              {featuredImagePreview && (
                <div className="mb-3">
                  <img
                    src={featuredImagePreview}
                    alt="Featured preview"
                    className="img-thumbnail"
                    style={{ maxHeight: '200px' }}
                  />
                </div>
              )}

              <h5 className="mt-4">Gallery</h5>
              <label className="btn btn-outline-secondary mb-3">
                <FiImage size={20} className="me-2" />
                {formData.galleryImages.length > 0 ? 'Add More Images' : 'Add Gallery Images'}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleGalleryImagesChange}
                  style={{ display: 'none' }}
                />
              </label>
              <p className="text-muted">Upload images for slider gallery post type (max 10MB each)</p>

              {galleryPreviews.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {galleryPreviews.map((preview, index) => (
                    <img
                      key={index}
                      src={preview}
                      alt={`Gallery preview ${index}`}
                      className="img-thumbnail"
                      style={{ height: '100px', width: '100px', objectFit: 'cover' }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="col-md-3">
            <div className="sidebar">
              <div className="side-post-title mb-3 p-2 bg-light rounded">
                <span>
                  <FiFileText size={18} className="me-2" />
                  {formData.title || 'No Title'}
                </span>
              </div>

              <div className="excert mt-3 mb-3">
                <div className="d-flex align-items-center mb-2">
                  <FiEdit3 size={16} className="me-2" />
                  <label>Excerpt</label>
                </div>
                <textarea
                  className="form-control"
                  name="excerpt"
                  placeholder="Add an excerpt…"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows="3"
                />
                <small className="text-muted">A short description of your post</small>
              </div>

              <ul className="list-unstyled tab-list mt-3 mb-3 p-2 bg-light rounded">
                <li className="mb-2">
                  <FiClock size={16} className="me-2" />
                  <span>Status:</span> <span className="fw-bold">{formData.status}</span>
                </li>
                <li>
                  <FiUser size={16} className="me-2" />
                  <span>Author:</span> <span className="fw-bold">Admin</span>
                </li>
              </ul>

              <div className="side-action">
                <div className="w-100 s-wrap mb-3">
                  <a
                    href="#categoriesCollapse"
                    className="d-flex justify-content-between align-items-center p-2 bg-light rounded text-decoration-none"
                    data-bs-toggle="collapse"
                  >
                    <span>
                      <FiFileText size={16} className="me-2" />
                      Categories
                    </span>
                    <FiChevronDown size={18} />
                  </a>
                  <div className="collapse mt-2" id="categoriesCollapse">
                    <div className="card card-body p-2">
                      {categories.length > 0 ? (
                        categories.map(category => (
                          <div key={category._id} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`cat-${category._id}`}
                              checked={formData.categories.includes(category._id)}
                              onChange={() => handleCategoryToggle(category._id)}
                            />
                            <label className="form-check-label" htmlFor={`cat-${category._id}`}>
                              {category.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No categories found</p>
                      )}
                      <button
                        className="btn btn-sm btn-link text-success p-0 mt-2 text-decoration-none"
                        onClick={handleAddNewCategory}
                      >
                        + Add New Category
                      </button>
                    </div>
                  </div>
                </div>

                <div className="w-100 s-wrap">
                  <a
                    href="#tagsCollapse"
                    className="d-flex justify-content-between align-items-center p-2 bg-light rounded text-decoration-none"
                    data-bs-toggle="collapse"
                  >
                    <span>
                      <FiFileText size={16} className="me-2" />
                      Tags
                    </span>
                    <FiChevronDown size={18} />
                  </a>
                  <div className="collapse mt-2" id="tagsCollapse">
                    <div className="card card-body p-2">
                      <div className="mb-3">
                        <label className="form-label">Add New Tag</label>
                        <div className="input-group">
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddNewTag();
                              }
                            }}
                            placeholder="Tag name"
                          />
                          <button
                            className="btn btn-sm btn-success"
                            onClick={handleAddNewTag}
                            disabled={!newTag.trim()}
                          >
                            Add
                          </button>
                        </div>
                        <small className="text-muted">Press Enter or click Add to create tag</small>
                      </div>

                      {tags.length > 0 ? (
                        <div className="tag-container">
                          {tags.map(tag => (
                            <div key={tag._id} className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`tag-${tag._id}`}
                                checked={formData.tags.includes(tag._id)}
                                onChange={() => handleTagToggle(tag._id)}
                              />
                              <label className="form-check-label" htmlFor={`tag-${tag._id}`}>
                                {tag.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted">No tags found</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePost;